/**
 * ReceiptClaimsAnalyticsSection — Phase R10.6 (2026-07-05)
 *
 * Additive section rendered below the existing <BrandAnalyticsPanel> on
 * /owner/analytics. Surfaces receipt-claim activity (approvals, amounts,
 * pending/rejected counts, stamps awarded) alongside the existing POS /
 * loyalty revenue metrics WITHOUT merging them.
 *
 * Data sources
 * ------------
 *   • NEW:    GET /api/owner/analytics/receipt-summary → 5-card strip
 *   • REUSE:  GET /api/owner/receipt-claims/branch-performance (R10.3)
 *             → branch-level breakdown table (<BranchPerformanceTable>)
 *
 * Range → date window
 * -------------------
 * Converts the Analytics page's existing range preset ("today" | "7d" |
 * "30d" | "90d") into inclusive `date_from` + `date_to` ISO dates (UTC)
 * targeting `ai_extraction.date` (receipt date, R10.3 consistency).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Receipt, CheckCircle2, Clock, XCircle, Coins } from 'lucide-react';

import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { formatRupiah } from '@/lib/claimErrorUI';
import BranchPerformanceTable from '@/components/owner/BranchPerformanceTable';

/**
 * Range → inclusive [date_from, date_to] window (UTC ISO date).
 *
 *   today → [today,     today]
 *   7d    → [today - 6, today]   (7-day inclusive window ending today)
 *   30d   → [today - 29, today]
 *   90d   → [today - 89, today]
 *
 * No `date_from` / `date_to` is sent when range is unknown.
 */
export function rangeToDateWindow(range) {
  const now = new Date();
  const todayIso = (() => {
    // UTC midnight-anchored YYYY-MM-DD so backend pattern r"^\d{4}-\d{2}-\d{2}$" accepts.
    const y = now.getUTCFullYear();
    const m = String(now.getUTCMonth() + 1).padStart(2, '0');
    const d = String(now.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })();
  const daysBack = { today: 0, '7d': 6, '30d': 29, '90d': 89 }[range];
  if (daysBack === undefined) return { date_from: undefined, date_to: undefined };
  if (daysBack === 0) return { date_from: todayIso, date_to: todayIso };
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - daysBack));
  const y = from.getUTCFullYear();
  const m = String(from.getUTCMonth() + 1).padStart(2, '0');
  const d = String(from.getUTCDate()).padStart(2, '0');
  return { date_from: `${y}-${m}-${d}`, date_to: todayIso };
}

function MetricCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 flex flex-col gap-1"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-white/50 font-semibold">
        <Icon size={12} className="text-gold" />
        {label}
      </div>
      <div className="text-xl font-bold text-white tabular-nums">{value}</div>
      {sub ? (
        <div className="text-[10px] text-white/40">{sub}</div>
      ) : null}
    </div>
  );
}

export default function ReceiptClaimsAnalyticsSection({ range = '7d' }) {
  const token = useAuthStore((s) => s.token);
  const { date_from, date_to } = useMemo(() => rangeToDateWindow(range), [range]);

  const summaryQ = useQuery({
    queryKey: ['owner-analytics-receipt-summary', date_from ?? 'all', date_to ?? 'all'],
    queryFn:  () => api.ownerAnalyticsReceiptSummary(token, { date_from, date_to }),
    enabled:  !!token,
    staleTime: 30_000,
  });

  const branchPerfQ = useQuery({
    queryKey: ['owner-analytics-branch-perf', date_from ?? 'all', date_to ?? 'all'],
    queryFn:  () => api.ownerBranchPerformance(token, { date_from, date_to }),
    enabled:  !!token,
    staleTime: 30_000,
  });

  const refetchAll = () => {
    summaryQ.refetch();
    branchPerfQ.refetch();
  };

  const s = summaryQ.data ?? {
    approved_count: 0, approved_total_amount: 0,
    pending_count: 0, rejected_count: 0,
    total_stamps_awarded_from_receipts: 0,
  };
  const rangeLabel = { today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 90 days', all: 'All time' }[range] ?? 'All time';
  const isFetching = summaryQ.isFetching || branchPerfQ.isFetching;

  return (
    <section
      className="rounded-3xl border border-white/10 bg-[#15151B] p-5 space-y-5"
      data-testid="owner-analytics-receipt-section"
    >
      {/* ------------- Section header --------------- */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-gold font-semibold mb-1">
            Receipt Claims
          </div>
          <h3 className="text-lg font-bold text-white">Receipt Claims Analytics</h3>
          <p className="text-xs text-white/55 mt-1 max-w-xl">
            Metrics from customer-submitted receipt claims. Not combined with
            POS revenue. Window: <span className="text-white/80 font-semibold">{rangeLabel}</span>.
          </p>
        </div>
        <button
          type="button"
          onClick={refetchAll}
          disabled={isFetching}
          data-testid="owner-analytics-receipt-refresh"
          className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 hover:border-gold/60 px-3 py-1.5 text-xs text-white/70 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* ------------- 5-card metric strip --------------- */}
      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-3"
        data-testid="owner-analytics-receipt-metrics"
      >
        <MetricCard
          icon={CheckCircle2}
          label="Approved Receipts"
          value={summaryQ.isLoading ? '—' : s.approved_count}
          sub="from receipt claims"
          testid="owner-analytics-receipt-approved-count"
        />
        <MetricCard
          icon={Receipt}
          label="Approved Receipt Amount"
          value={summaryQ.isLoading ? '—' : formatRupiah(s.approved_total_amount)}
          sub="from receipt claims"
          testid="owner-analytics-receipt-approved-amount"
        />
        <MetricCard
          icon={Clock}
          label="Pending Claims"
          value={summaryQ.isLoading ? '—' : s.pending_count}
          sub="awaiting review"
          testid="owner-analytics-receipt-pending-count"
        />
        <MetricCard
          icon={XCircle}
          label="Rejected Claims"
          value={summaryQ.isLoading ? '—' : s.rejected_count}
          sub="from receipt claims"
          testid="owner-analytics-receipt-rejected-count"
        />
        <MetricCard
          icon={Coins}
          label="Stamps Awarded"
          value={summaryQ.isLoading ? '—' : s.total_stamps_awarded_from_receipts}
          sub="from receipt claims"
          testid="owner-analytics-receipt-stamps-awarded"
        />
      </div>

      {summaryQ.isError ? (
        <div
          className="rounded-xl border border-red-400/30 bg-red-400/10 p-3 text-xs text-red-200"
          data-testid="owner-analytics-receipt-summary-error"
        >
          Could not load receipt summary.
        </div>
      ) : null}

      {/* ------------- Branch-level breakdown table --------------- */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold">
            Branch Performance (receipt claims)
          </div>
          <div className="text-[10px] text-white/40">
            Click a column header to sort
          </div>
        </div>
        <div
          className="rounded-2xl border border-white/5 bg-white/[0.02] p-2"
          data-testid="owner-analytics-receipt-branch-perf"
        >
          <BranchPerformanceTable
            rows={branchPerfQ.data?.branches ?? []}
            isLoading={branchPerfQ.isLoading}
            isError={branchPerfQ.isError}
          />
        </div>
      </div>
    </section>
  );
}
